import { Delete, Edit } from "@material-ui/icons";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMemo, useState } from 'react';
import { Button, Col, Row } from "react-bootstrap";
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
    useCreateDivisionMutation,
    useDeleteDivisionMutation,
    useGetAllDivisionsQuery,
    useGetDivisionDriverNumberQuery,
    useLazyGetDivisionByIdQuery,
    useUpdateDivisionMutation
} from "../../../features/admin/divisions/divisionSlice";
import Wrapper from '../../Wrapper';
import Dialog from "../../designSystem/Dialog";
import Select from "../../designSystem/Select/Select";
import TextField from '../../designSystem/TextField';
import errorHandler from '../../hooks/ErrorHanlder';

const Divisions = () => {
    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        defaultValues: { divisionName: '', divisionCode: '', divisionId: '' }
    });
    const [editingDivisionId, setEditingDivisionId] = useState(null);
    const [divisionToDelete, setDivisionToDelete] = useState(null);
    const { data: divisionList, refetch } = useGetAllDivisionsQuery();
    const { data: driverList } = useGetDivisionDriverNumberQuery();
    const [createDivision] = useCreateDivisionMutation();
    const [updateDivision] = useUpdateDivisionMutation();
    const [deleteDivision] = useDeleteDivisionMutation();
    const [fetchDivision, { data: divisionData }] =
        useLazyGetDivisionByIdQuery();
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const handleCancel = () => {
        reset({ divisionCode: '', divisionId: "", divisionName: "" });                // reset to defaultValues
        setEditingDivisionId(null);
    };
    const driverOptions = useMemo(() => {
        if (!divisionData?.data) return driverList?.data ?? [];
        const { driverID, entityCode } = divisionData.data;
        // if the driver is already in the unassigned list, just return it
        if (driverList.data?.some(d => d.empDriverId === driverID)) {
            return driverList.data;
        }

        // otherwise append it
        return [
            ...(driverList.data || []),
            { empDriverId: driverID, driverNumber: entityCode },
        ];
    }, [driverList, divisionData]);


    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setDivisionToDelete(null);
    };

    const deleteHandler = async () => {
        try {
            await deleteDivision(divisionToDelete).unwrap();
            toast.success("Deleted Successfully", { position: 'top-center' });
            await refetch();
            handleCancel()
        } catch (err) {
            errorHandler(err);
        }
        handleCloseDeleteDialog();
    };

    const handleEditClick = async (row) => {
        setEditingDivisionId(row.id);

        const { data } = await fetchDivision(row.id).unwrap();
        reset({
            divisionName: data.divisionName,
            divisionCode: data.divisionCode,
            divisionId: data.driverID,
        });
    };

    const handleSubmitDivision = handleSubmit(async (formData) => {

        const isEdit = Boolean(editingDivisionId);
        const mutate = isEdit ? updateDivision : createDivision;
        const body = isEdit
            ? { ...formData, divisionId: editingDivisionId, driverID: divisionData?.data.driverID, entityCode: divisionData?.data.driverID.entityCode }
            : { ...formData, driverId: formData.divisionId };

        const successMsg = isEdit
            ? "Division Updated Successfully"
            : "Division Created Successfully";

        try {
            await mutate(body).unwrap();
            toast.success(successMsg, { position: 'top-center' });
            refetch();
            handleCancel();
        } catch (error) {
            errorHandler(error);
        }
    });

    const columns = [
        {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            width: 120,
            disableClickEventBubbling: true,
            renderCell: (params) => {
                const onClickEdit = (e) => {
                    e.stopPropagation();
                    handleEditClick(params.row);

                };
                const onRemove = (e) => {
                    e.stopPropagation();
                    setDivisionToDelete(params.row.id);
                    setOpenDeleteDialog(true);
                };
                return (
                    <div className="action_btn">
                        <span className="edit_btn" onClick={onClickEdit}><Edit /></span>
                        <span className="del_btn" onClick={onRemove}><Delete /></span>
                    </div>
                );
            },
        },
        { field: 'divisionName', headerName: 'Division Name', width: 180 },
        { field: 'divisionCode', headerName: 'Division Code', width: 110 },
    ];

    return (
        <Wrapper>
            <h3>Divisions</h3>
            <Row className='d-flex justify-content-end mb-3'>
                <Col sm={"auto"}>
                    <Button
                        onClick={handleSubmit(handleSubmitDivision)}
                    >
                        {editingDivisionId ? "Update Division" : "Add Division"}
                    </Button>
                </Col>
                {editingDivisionId ? <Col sm="auto" className="ps-0">
                    <Button onClick={handleCancel} variant='outline-primary' style={{ minWidth: "100px" }}>
                        Cancel
                    </Button>
                </Col> : null}
            </Row>
            <fieldset className="mb-3">
                <Row className='mt-3 mb-3'>
                    <Col sm={4}>
                        <Select
                            {...register('divisionId', { required: 'Field is required' })}
                            label="Division ID"
                            options={driverOptions?.map(d => ({
                                value: d.empDriverId,
                                label: d.driverNumber
                            }))}
                            error={errors.divisionId}
                        />
                    </Col>
                    <Col sm={4}>
                        <TextField label="Division Name"  {...register('divisionName', { required: 'Field is required' })} error={errors.divisionName?.message} />
                    </Col>
                    <Col sm={4}>
                        <TextField label="Division Code"  {...register('divisionCode', { required: 'Field is required' })} error={errors.divisionCode?.message} />
                    </Col>
                </Row>
            </fieldset>
            <div style={{ height: "calc(100vh - 380px)" }}>
                <DataGrid
                    rows={
                        divisionList?.data?.map((division) => {
                            return {
                                id: division.divisionId,
                                divisionName: division.divisionName,
                                divisionCode: division.divisionCode,
                                driverID: divisionData?.data?.driverID,
                            };
                        }) || []
                    }
                    columns={columns}
                    pageSize={10}
                    sx={{ padding: "0px !important" }}
                    components={{ Toolbar: GridToolbar }}
                />
            </div>
            <Dialog title="Delete Confirmation" show={openDeleteDialog} onClose={handleCloseDeleteDialog} description="Are you sure you want to delete this row?" onSubmit={deleteHandler} />
        </Wrapper>
    );
};

export default Divisions;